import React, { useState, useEffect } from 'react'
import {
  Card,
  Typography,
  Table,
  Button,
  Tag,
  Space,
  Empty,
  Modal,
  Form,
  Input,
  Select,
  InputNumber,
  theme,
  Divider,
  DatePicker,
  Switch,
  message
} from 'antd'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faEdit, faClipboardList, faPlus, faTrash } from '@fortawesome/free-solid-svg-icons'
import dayjs from 'dayjs'
import { formatCurrency } from './utils'
import { GlobalServicesEnums } from '../../../../../Super-Admin/Global Services/utils/enums'
import { handleError, hexToRGBA } from '../../../../../lib/utils'
import BillingTool from '../../../../../Bookings/components/Examination Components/BillingTool'

const { Title, Text } = Typography
const { TextArea } = Input

/**
 * Custom Service Creation/Edit Modal Component
 */
export const CustomServiceModal = ({ visible, service, onCancel, onSave, currentUser }) => {
  const [form] = Form.useForm()
  const [loading, setLoading] = useState(false)
  const [billingItems, setBillingItems] = useState([])
  const [initializedBillingItems, setInitializedBillingItems] = useState(false)
  const [billingRecordsLoading, setBillingRecordsLoading] = useState(true)

  useEffect(() => {
    if (visible) {
      if (service) {
        // Editing existing custom service
        const serviceData = {
          ...service,
          createdAt: service.audit?.createdAt ? dayjs(service.audit.createdAt) : null,
          modifiedAt: service.audit?.modifiedAt ? dayjs(service.audit.modifiedAt) : null,
          createdBy: service.audit?.createdBy || '',
          modifiedBy: service.audit?.modifiedBy || ''
        }
        form.setFieldsValue(serviceData)

        // Initialize billing items
        if (!initializedBillingItems) {
          const existingItems =
            service?.billingMacroItems && Array.isArray(service.billingMacroItems) ? service.billingMacroItems : []
          setBillingItems(existingItems)
          setInitializedBillingItems(true)
          setTimeout(() => setBillingRecordsLoading(false), 100)
        }
      } else {
        // Creating new custom service
        const now = dayjs()
        const defaultValues = {
          name: '',
          category: '',
          rate: '',
          durationMin: '',
          description: '',
          billingCode: '',
          deliveryModes: [''],
          facilityTypes: [''],
          tags: [''],
          requiredPractitionerType: '',
          billables: [],
          billingMacroItems: [],
          active: true,
          isCustom: true,
          createdAt: now,
          createdBy: currentUser?.name || 'System',
          modifiedAt: now,
          modifiedBy: currentUser?.name || 'System'
        }
        form.setFieldsValue(defaultValues)

        if (!initializedBillingItems) {
          setBillingItems([])
          setInitializedBillingItems(true)
          setTimeout(() => setBillingRecordsLoading(false), 100)
        }
      }
    } else if (!visible) {
      setInitializedBillingItems(false)
      setBillingRecordsLoading(true)
    }
  }, [visible, service, form, initializedBillingItems, currentUser])

  const handleSave = async () => {
    try {
      setLoading(true)
      const values = await form.validateFields()

      // Format audit data
      const auditData = {
        createdAt: values.createdAt?.toISOString() || dayjs().toISOString(),
        createdBy: values.createdBy || currentUser?.name || 'System',
        modifiedAt: dayjs().toISOString(),
        modifiedBy: currentUser?.name || 'System'
      }

      const serviceData = {
        ...values,
        isCustom: true,
        audit: auditData,
        billingMacroItems: billingItems.filter((item) => item.name)
      }

      onSave(serviceData)
      setLoading(false)
    } catch (error) {
      message.error(handleError(error, true))
      setLoading(false)
    }
  }

  const handleBillingItemSelection = (items) => {
    setBillingItems(items)
    form.setFieldValue(
      'billingMacroItems',
      items.filter((item) => item.name)
    )
  }

  const { token } = theme.useToken()

  return (
    <Modal
      title={
        <Space>
          <FontAwesomeIcon icon={service ? faEdit : faPlus} style={{ color: '#1890ff' }} />
          <span>{service ? `Edit Custom Service: ${service.name}` : 'Create Custom Service'}</span>
        </Space>
      }
      open={visible}
      onCancel={onCancel}
      onOk={handleSave}
      okButtonProps={{ style: { background: token.colorSuccess } }}
      okText={service ? 'Update Service' : 'Create Service'}
      confirmLoading={loading}
      width={1300}
      destroyOnClose
      style={{ top: 20 }}
    >
      <Form form={form} layout='vertical' style={{ marginTop: '20px' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
          {/* Service Name */}
          <Form.Item
            name='name'
            label='Service Name'
            rules={[
              {
                required: true,
                message: 'Please enter a descriptive service name for your custom service.'
              }
            ]}
          >
            <Input placeholder='Enter custom service name' />
          </Form.Item>

          {/* Category */}
          <Form.Item
            name='category'
            label='Category'
            rules={[
              {
                required: true,
                message: 'Please select a category for this custom service.'
              }
            ]}
          >
            <Select placeholder='Select service category'>
              {GlobalServicesEnums.categories.map((category) => (
                <Select.Option key={category.value} value={category.value}>
                  <Space>
                    <span>{category.icon}</span>
                    <span>{category.label}</span>
                  </Space>
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          {/* Status */}
          <Form.Item
            name='active'
            label='Status'
            rules={[
              {
                required: true,
                message: 'Please select whether this service is active or inactive.'
              }
            ]}
          >
            <Select>
              <Select.Option value={true}>Active</Select.Option>
              <Select.Option value={false}>Inactive</Select.Option>
            </Select>
          </Form.Item>

          {/* Cost */}
          <Form.Item
            name='rate'
            label='Cost (ZAR)'
            rules={[
              {
                required: true,
                message: 'Please enter the service cost in South African Rand.'
              }
            ]}
          >
            <InputNumber
              min={0}
              style={{ width: '100%' }}
              placeholder='Enter cost'
              formatter={(value) => `R ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
              parser={(value) => value.replace(/R\s?|(,*)/g, '')}
            />
          </Form.Item>

          {/* Duration */}
          <Form.Item
            name='durationMin'
            label='Duration (minutes)'
            rules={[
              {
                required: true,
                message: 'Please enter the expected duration for this service in minutes.'
              }
            ]}
          >
            <InputNumber min={1} style={{ width: '100%' }} placeholder='Enter duration in minutes' />
          </Form.Item>

          {/* Required Practitioner Type */}
          <Form.Item
            name='requiredPractitionerType'
            label='Required Practitioner Type'
            rules={[
              {
                required: true,
                message: 'Please select the type of healthcare practitioner required.'
              }
            ]}
          >
            <Select placeholder='Select practitioner type'>
              <Select.Option value='GP'>General Practitioner</Select.Option>
              <Select.Option value='Nurse'>Nurse</Select.Option>
              <Select.Option value='Specialist'>Specialist</Select.Option>
              <Select.Option value='Allied Health'>Allied Health</Select.Option>
              <Select.Option value='Pharmacist'>Pharmacist</Select.Option>
              <Select.Option value='Administrator'>Administrator</Select.Option>
            </Select>
          </Form.Item>
        </div>

        {/* Description */}
        <Form.Item name='description' label='Description (Patient/Admin Facing)'>
          <TextArea rows={3} placeholder='Enter service description (optional)' />
        </Form.Item>
        <Space>
          <Text>Please select whether this service shows as a shortcut procedure for billing.</Text>
          <Form.Item style={{ marginBottom: '0px' }} name='procedureShortcut'>
            <Switch unCheckedChildren='Exclude' checkedChildren='Include' />
          </Form.Item>
        </Space>

        {/* Billing Items Section */}
        <Card
          style={{
            borderRadius: '12px',
            marginTop: '24px',
            border: `1px solid ${hexToRGBA('#1890ff', 0.2)}`,
            boxShadow: '0 4px 12px rgba(0,0,0,0.05)'
          }}
          bodyStyle={{ padding: '0' }}
        >
          <div style={{ padding: '24px 24px 0 24px' }}>
            <Title level={5} style={{ margin: '0 0 16px 0', color: '#1f1f1f' }}>
              <FontAwesomeIcon icon={faClipboardList} style={{ marginRight: '8px', color: '#52c41a' }} />
              Service Billing Components
            </Title>
            <Text type='secondary' style={{ fontSize: '14px', marginBottom: '20px', display: 'block' }}>
              Configure the specific billing items, procedures, materials, and medicines included when this service is
              provided
            </Text>
          </div>

          <div style={{ background: hexToRGBA('#f5f5f5', 0.3), padding: '20px' }}>
            <BillingTool
              key={`custom-billing-tool-${service?._id || 'new'}-${visible}-${initializedBillingItems}`}
              billing={{}}
              itemSelection={billingItems}
              setItemSelection={handleBillingItemSelection}
              readOnly={false}
              isMacroForm={true}
              renderPrivatePrice={true}
              billingRecordsLoading={billingRecordsLoading}
              hideAdd={false}
              bookingData={{}}
            />
          </div>
        </Card>

        {/* Hidden form fields */}
        <Form.Item name='billingMacroItems' style={{ display: 'none' }}>
          <Input type='hidden' />
        </Form.Item>
        <Form.Item name='isCustom' style={{ display: 'none' }}>
          <Input type='hidden' />
        </Form.Item>
        <Form.Item name='modifiedBy' style={{ display: 'none' }}>
          <Input type='hidden' />
        </Form.Item>
      </Form>
    </Modal>
  )
}

/**
 * Custom Services Table Columns Definition
 */
export const getCustomServiceColumns = (handleEditCustomService, handleDeleteCustomService) => [
  {
    title: 'Service',
    key: 'service',
    render: (_, service) => (
      <Space direction='vertical' size={2}>
        <Text strong style={{ fontSize: '14px' }}>
          {service.name}
        </Text>
        <Space>
          <Tag color='purple' size='small'>
            Custom
          </Tag>
          <Text style={{ fontSize: '12px', color: '#666' }}>
            {GlobalServicesEnums.categories.find((c) => c.value === service.category)?.label}
          </Text>
        </Space>
      </Space>
    )
  },
  {
    title: 'Cost',
    dataIndex: 'rate',
    key: 'cost',
    width: '120px',
    render: (rate) => <Text>{rate ? formatCurrency(rate) : '-'}</Text>
  },
  {
    title: 'Duration',
    dataIndex: 'durationMin',
    key: 'duration',
    width: '100px',
    render: (duration) => <Text>{duration ? duration + ' min' : '-'}</Text>
  },
  {
    title: 'Status',
    key: 'status',
    width: '140px',
    render: (_, service) => (
      <Tag color={service.active ? 'success' : 'default'}>{service.active ? 'Active' : 'Inactive'}</Tag>
    )
  },
  {
    title: 'Billing Items',
    key: 'billingMacroItems',
    width: '120px',
    render: (_, service) => {
      const billingItemsCount = service?.billingMacroItems
        ? service.billingMacroItems.filter((item) => item.type && item.code && item.name).length
        : 0

      if (billingItemsCount > 0) {
        return (
          <Tag color='success' icon={<FontAwesomeIcon icon={faClipboardList} />}>
            {billingItemsCount} Items
          </Tag>
        )
      } else {
        return <Tag color='default'>No Items</Tag>
      }
    }
  },
  {
    title: 'Actions',
    key: 'actions',
    width: '160px',
    render: (_, service) => (
      <Space>
        <Button
          type='primary'
          size='small'
          onClick={() => handleEditCustomService(service)}
          icon={<FontAwesomeIcon icon={faEdit} />}
        >
          Edit
        </Button>
        {service._id && (
          <Button
            type='text'
            size='small'
            danger
            onClick={() => handleDeleteCustomService(service._id)}
            icon={<FontAwesomeIcon icon={faTrash} />}
          />
        )}
      </Space>
    )
  }
]

/**
 * Custom Services Management Hook
 */
export const useCustomServices = (recordData, handleValuesChange, currentUser) => {
  const [customServiceModalVisible, setCustomServiceModalVisible] = useState(false)
  const [selectedCustomService, setSelectedCustomService] = useState(null)

  // Filter custom services from the main servicesConfig array
  const servicesConfig = recordData?.servicesConfig || []
  const customServices = servicesConfig.filter((service) => service.isCustom === true)

  const handleCreateCustomService = () => {
    setSelectedCustomService(null)
    setCustomServiceModalVisible(true)
  }

  const handleEditCustomService = (service) => {
    setSelectedCustomService(service)
    setCustomServiceModalVisible(true)
  }

  const handleSaveCustomService = (serviceData) => {
    const currentServicesConfig = recordData?.servicesConfig || []

    if (selectedCustomService) {
      // Update existing custom service
      const updatedServicesConfig = currentServicesConfig.map((service) =>
        service._id === selectedCustomService._id ? { ...serviceData, _id: selectedCustomService._id } : service
      )
      handleValuesChange({ servicesConfig: updatedServicesConfig })
    } else {
      // Create new custom service
      const newService = {
        ...serviceData,
        isCustom: true
        // Note: No serviceRef for custom services, no _id - let MongoDB handle it
      }
      const updatedServicesConfig = [...currentServicesConfig, newService]
      handleValuesChange({ servicesConfig: updatedServicesConfig })
    }
    setCustomServiceModalVisible(false)
    setSelectedCustomService(null)
  }

  const handleDeleteCustomService = (serviceId) => {
    Modal.confirm({
      title: 'Delete Custom Service',
      content: 'Are you sure you want to delete this custom service? This action cannot be undone.',
      okText: 'Delete',
      okType: 'danger',
      onOk: () => {
        const currentServicesConfig = recordData?.servicesConfig || []
        const updatedServicesConfig = currentServicesConfig.filter((service) => service._id !== serviceId)
        handleValuesChange({ servicesConfig: updatedServicesConfig })
      }
    })
  }

  const handleCloseModal = () => {
    setCustomServiceModalVisible(false)
    setSelectedCustomService(null)
  }

  return {
    customServices,
    customServiceModalVisible,
    selectedCustomService,
    handleCreateCustomService,
    handleEditCustomService,
    handleSaveCustomService,
    handleDeleteCustomService,
    handleCloseModal,
    currentUser
  }
}

/**
 * Custom Services Section Component
 */
export const CustomServicesSection = ({
  customServices,
  handleCreateCustomService,
  handleEditCustomService,
  handleDeleteCustomService
}) => {
  const customServiceColumns = getCustomServiceColumns(handleEditCustomService, handleDeleteCustomService)

  return (
    <Card
      style={{
        marginBottom: '20px',
        borderRadius: '8px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
      }}
      bodyStyle={{ padding: '20px' }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
        <div>
          <Title level={4} style={{ margin: 0, color: '#1f1f1f' }}>
            Custom Services
          </Title>
          <Text style={{ color: '#666666', fontSize: '14px' }}>Create and manage services specific to your clinic</Text>
        </div>
        <Button type='primary' icon={<FontAwesomeIcon icon={faPlus} />} onClick={handleCreateCustomService}>
          Add Custom Service
        </Button>
      </div>

      {customServices.length > 0 ? (
        <Table
          dataSource={customServices}
          columns={customServiceColumns}
          rowKey={(record, index) => record._id || `temp-${index}`}
          pagination={{
            pageSize: 5,
            showSizeChanger: false,
            showQuickJumper: false,
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} custom services`
          }}
          style={{ borderRadius: '8px' }}
        />
      ) : (
        <Empty
          description={
            <span>
              No custom services created yet.
              <br />
              Click "Add Custom Service" to create services specific to your clinic.
            </span>
          }
        />
      )}
    </Card>
  )
}
